/**
 * 업무 프로젝트 간트 보기 — 업무를 시작일~마감일 가로 막대로(색 = 유형 색). 단위 일·주·월·분기 ·
 * 오늘선 · 배율. 막대(또는 왼쪽 열 제목)를 누르면 업무 상세. 리스트의 200건 목록과 따로 보이는
 * 범위만 서버에 묻는다(캘린더와 같다).
 *
 * - 🔴 날짜는 'YYYY-MM-DD' 와 정오(12시) Date 로만 계산한다 — toISOString() 금지
 *   (KST 0~9시에 하루 어긋나고, 서머타임 없는 한국에서도 월 경계가 밀린다).
 * - 🔴 스크롤 컨테이너는 하나다 — 날짜 머리는 sticky top, 왼쪽 «상태 / 업무» 열은 sticky left,
 *   모서리 칸은 둘 다. 머리·본문을 따로 스크롤시키면 가로 위치가 어긋난다.
 * - 🔴 scrollLeft 대입만 쓴다 — scrollTo·scrollIntoView 는 jsdom 에 없어 시험이 TypeError 로 죽는다.
 * - 막대 끌기(J4c1b): 몸통 = 이동 · 양끝 = 한쪽 날짜 · 열기는 onClick 하나(끈 뒤 click 은 삼킨다).
 * - 🔴 끌기 이동량은 CSS px 로 잰다 — 웹 배율(<html> zoom)이면 clientX 는 화면 px 라 그대로 나누면
 *   날짜가 배율만큼 빨리 움직인다. 누를 때 스크롤 상자의 scaleOf 를 재 두고 나눈다(jsdom 은 1).
 * - 모바일(useIsMobile)은 왼쪽 열 120 · 머리 «업무» · 키 없음 · 처음 위치는 고정 열 오른쪽 기준(#2687).
 *   왼쪽 칸은 글자가 아니라 data-gantt-leftcol 표지로 찾는다(모바일에서 머리 글자가 바뀐다).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { errorText } from './errorText';
import { taskPmAPI } from '../../api/client';
import { scaleOf } from '../../lib/floatingBounds';
import { COLORS, RADIUS, btn, card } from '../../utils/uiStyles';
import useIsMobile from '../../hooks/useIsMobile';

// 보이는 범위만 묻지만 그 안에 업무가 많을 수 있다 → 서버 상한(500)까지 받는다.
const GANTT_LIMIT = 500;

// 잔디 실측 치수 — 왼쪽 열 240(머리 61 = 위 30 + 아래 31) · 업무 행 28 · 묶음 머리 38.
const LEFT_WIDTH = 240;
const LEFT_WIDTH_MOBILE = 120;   // 모바일(≤768·터치) — 375 레이아웃에서 타임라인 221(#2687)
const HEAD_TOP_HEIGHT = 30;
const HEAD_BOTTOM_HEIGHT = 31;
const HEAD_HEIGHT = HEAD_TOP_HEIGHT + HEAD_BOTTOM_HEIGHT;
const ROW_HEIGHT = 28;
const GROUP_HEIGHT = 38;
const BAR_HEIGHT = 20;
const BAR_RADIUS = 5;
const HANDLE_WIDTH = 6;      // 막대 양끝 손잡이(잔디와 같다)

const WEEKDAY_CHARS = ['일', '월', '화', '수', '목', '금', '토'];

export const UNITS = [
  { value: 'day', label: '일' },
  { value: 'week', label: '주' },
  { value: 'month', label: '월' },
  { value: 'quarter', label: '분기' },
];
// 잔디 실측 — 일 32/일 · 주 200/주 · 월 100/월 · 분기 75/분기(평균 달 30.4375일 · 평균 분기 91.3125일).
export const PX_PER_DAY = {
  day: 32,
  week: 200 / 7,
  month: 100 / 30.4375,
  quarter: 75 / 91.3125,
};
export const ZOOMS = [50, 75, 100, 125, 150, 200];

// ───────────────────────────────────────────── 날짜 도우미(순수)

const pad2 = (n) => String(n).padStart(2, '0');

// 🔴 오늘은 브라우저 '로컬' 날짜로 만든다 — toISOString() 은 KST 0~9시에 하루 어긋난다.
export function localTodayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

// 'YYYY-MM-DD' → 그날 정오의 Date(정오라 일광절약·시간대 보정에도 날짜가 안 넘어간다).
export function toDate(key) {
  const s = String(key);
  return new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)), 12);
}

// Date → 로컬 'YYYY-MM-DD'
export function keyOf(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function addDays(key, n) {
  const d = toDate(key);
  d.setDate(d.getDate() + n);
  return keyOf(d);
}

export function diffDays(a, b) {
  return Math.round((toDate(b).getTime() - toDate(a).getTime()) / 86400000);
}

/**
 * ISO 8601 주차 — 그 주 목요일이 속한 해가 연도이고, 1주는 1월 4일이 든 주다.
 * (잔디 간트 위 줄의 «39주» 가 이 규칙이다 — 2026-09-21 = 39주.)
 */
export function isoWeek(key) {
  const monday = mondayOf(key);
  const year = Number(addDays(monday, 3).slice(0, 4));     // 그 주 목요일의 해
  const firstMonday = mondayOf(`${year}-01-04`);           // 1월 4일이 든 주의 월요일 = 1주
  return { year, week: 1 + Math.round(diffDays(firstMonday, monday) / 7) };
}

function mondayOf(key) {
  const dow = toDate(key).getDay();          // 0=일 … 6=토
  return addDays(key, -((dow + 6) % 7));
}

// 눈금 한 칸의 '첫날' — step 단위로 내림한다.
function unitStartOf(key, step) {
  const y = Number(String(key).slice(0, 4));
  const m = Number(String(key).slice(5, 7));
  if (step === 'day') return String(key);
  if (step === 'week') return mondayOf(key);
  if (step === 'month') return `${y}-${pad2(m)}-01`;
  if (step === 'quarter') return `${y}-${pad2(Math.floor((m - 1) / 3) * 3 + 1)}-01`;
  return `${y}-01-01`;                        // year
}

function nextUnitStart(key, step) {
  const y = Number(String(key).slice(0, 4));
  const m = Number(String(key).slice(5, 7));
  if (step === 'day') return addDays(key, 1);
  if (step === 'week') return addDays(key, 7);
  if (step === 'month') return keyOf(new Date(y, m, 1, 12));
  if (step === 'quarter') return keyOf(new Date(y, m + 2, 1, 12));
  return keyOf(new Date(y + 1, 0, 1, 12));    // year
}

// [from, to] 를 step 단위로 자른다. 범위 처음·끝에 걸친 칸은 '잘린 폭' 그대로 둔다.
function spans(from, to, step) {
  const out = [];
  let cursor = unitStartOf(from, step);
  while (cursor <= to) {
    const next = nextUnitStart(cursor, step);
    const last = addDays(next, -1);
    out.push({ unitStart: cursor, start: cursor < from ? from : cursor, end: last > to ? to : last });
    cursor = next;
  }
  return out;
}

const toCells = (list, from, px, make) => list.map((s) => ({
  key: s.unitStart,
  left: diffDays(from, s.start) * px,
  width: (diffDays(s.start, s.end) + 1) * px,
  ...make(s),
}));

const quarterOf = (key) => Math.floor((Number(String(key).slice(5, 7)) - 1) / 3) + 1;
const yearOf = (key) => Number(String(key).slice(0, 4));
const monthOf = (key) => Number(String(key).slice(5, 7));

/**
 * 눈금 한 벌을 만든다.
 *   { from, to, pxPerDay, width, top: [{key,label,left,width}], bottom: [{key,label,sub,left,width,weekend}] }
 * 범위는 '오늘이 든 칸 + 앞 N칸 + 뒤 M칸' 이다(일 4달 · 주 26주 · 월 24달 · 분기 12분기).
 */
export function buildScale(unit, todayKey, zoom = 100) {
  const px = (PX_PER_DAY[unit] || PX_PER_DAY.month) * (zoom || 100) / 100;
  const y = yearOf(todayKey);
  const m = monthOf(todayKey);

  let from;
  let to;
  if (unit === 'day') {
    from = keyOf(new Date(y, m - 2, 1, 12));                       // 앞 1달 1일
    to = addDays(keyOf(new Date(y, m + 2, 1, 12)), -1);            // 뒤 2달 말일
  } else if (unit === 'week') {
    from = addDays(mondayOf(todayKey), -56);                       // 앞 8주 월요일
    to = addDays(from, 26 * 7 - 1);                                // 26주
  } else if (unit === 'quarter') {
    const qs = unitStartOf(todayKey, 'quarter');
    from = keyOf(new Date(yearOf(qs), monthOf(qs) - 1 - 12, 1, 12));        // 앞 4분기
    to = addDays(keyOf(new Date(yearOf(qs), monthOf(qs) - 1 + 24, 1, 12)), -1); // 뒤 7분기 말일
  } else {
    from = keyOf(new Date(y, m - 1 - 6, 1, 12));                   // 앞 6달 1일
    to = addDays(keyOf(new Date(y, m - 1 + 18, 1, 12)), -1);       // 뒤 17달 말일
  }

  let top;
  let bottom;
  if (unit === 'day' || unit === 'week') {
    bottom = toCells(spans(from, to, 'day'), from, px, (s) => {
      const dow = toDate(s.unitStart).getDay();
      return {
        label: String(toDate(s.unitStart).getDate()),
        sub: WEEKDAY_CHARS[dow],
        weekend: dow === 0 || dow === 6,
      };
    });
    top = unit === 'day'
      ? toCells(spans(from, to, 'month'), from, px, (s) => ({ label: `${yearOf(s.unitStart)}년 ${monthOf(s.unitStart)}월` }))
      : toCells(spans(from, to, 'week'), from, px, (s) => ({
        label: `${yearOf(s.unitStart)} ${monthOf(s.unitStart)}월 ${isoWeek(s.unitStart).week}주`,
      }));
  } else if (unit === 'month') {
    bottom = toCells(spans(from, to, 'month'), from, px, (s) => ({ label: `${monthOf(s.unitStart)}월`, sub: '', weekend: false }));
    top = toCells(spans(from, to, 'quarter'), from, px, (s) => ({ label: `${yearOf(s.unitStart)} ${quarterOf(s.unitStart)}분기` }));
  } else {
    bottom = toCells(spans(from, to, 'quarter'), from, px, (s) => ({ label: `${quarterOf(s.unitStart)}분기`, sub: '', weekend: false }));
    top = toCells(spans(from, to, 'year'), from, px, (s) => ({ label: `${yearOf(s.unitStart)}` }));
  }

  return { from, to, pxPerDay: px, width: (diffDays(from, to) + 1) * px, top, bottom };
}

/**
 * 막대가 덮는 날짜. 🔴 한쪽만 있으면 나머지를 그 값으로 메운다(하루 막대) — aria-label 도 이 값을 쓴다.
 * 둘 다 없으면 null(서버가 그런 업무를 빼 주지만 방어로 남긴다).
 */
export function barDates(task) {
  const start = task?.start_date || task?.due_date;
  const end = task?.due_date || task?.start_date;
  if (!start || !end) return null;
  return { start: String(start).slice(0, 10), end: String(end).slice(0, 10) };
}

// {start,end} → 눈금 위 자리. 끄는 중에는 '끝나면 될' 날짜로 그려야 해서 날짜를 직접 받는다.
export function geometryOf(dates, scale) {
  if (!dates || !scale) return null;
  const px = scale.pxPerDay;
  return {
    left: diffDays(scale.from, dates.start) * px,
    // 아주 좁은 배율에서도 눌러 볼 수 있게 최소 6px 은 남긴다.
    width: Math.max(6, (diffDays(dates.start, dates.end) + 1) * px),
  };
}

export function barGeometry(task, scale) {
  return geometryOf(barDates(task), scale);
}

/**
 * 끄는 중(또는 저장할) 날짜. 🔴 손잡이는 한쪽만 움직이고 반대편을 넘지 못한다(시작 ≤ 마감).
 *   move = 둘 다 +delta · start = min(시작+delta, 마감) · end = max(마감+delta, 시작)
 */
export function previewDates(drag) {
  if (!drag) return null;
  const { mode, origStart, origEnd } = drag;
  const n = drag.deltaDays || 0;
  if (mode === 'start') {
    const start = addDays(origStart, n);
    return { start: start > origEnd ? origEnd : start, end: origEnd };
  }
  if (mode === 'end') {
    const end = addDays(origEnd, n);
    return { start: origStart, end: end < origStart ? origStart : end };
  }
  return { start: addDays(origStart, n), end: addDays(origEnd, n) };
}

// ───────────────────────────────────────────── 화면

export default function TaskPmGantt({
  projectId, quick, q, refreshKey, optionMap, statuses = [], onOpenTask,
  canEdit = false, onChanged, todayKey = localTodayKey(),
}) {
  const [unit, setUnit] = useState('month');       // 잔디 기본 단위 = 월
  const [zoom, setZoom] = useState(100);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  // 🔴 저장 실패 문구는 불러오기 오류(error)와 따로 둔다 — 아래 불러오기 effect 가 보낼 때 error 를
  //    비우므로, 같은 상태를 쓰면 되돌림 뒤 다시 읽기가 시작되자마자 문구가 지워진다.
  const [saveError, setSaveError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);   // 저장 실패 뒤 서버 값으로 다시 읽기
  const [drag, setDrag] = useState(null);          // 그리기용 복사본(값 정본은 dragRef)
  // 늦게 온 응답이 최신 눈금을 덮지 않게 요청마다 번호를 매긴다.
  const seqRef = useRef(0);
  const scrollRef = useRef(null);
  // 끌기 값 정본 — { id, mode, startX, rootScale, origStart, origEnd, hadStart, hadDue, deltaDays }
  const dragRef = useRef(null);
  const savingRef = useRef(new Set());             // 저장 중인 업무 id(같은 막대 다시 끌기 금지)
  const suppressClickRef = useRef(false);          // 끈 뒤 브라우저가 보내는 click 한 번을 삼킨다

  const scale = useMemo(() => buildScale(unit, todayKey, zoom), [unit, todayKey, zoom]);
  // 모바일은 왼쪽 고정 열을 «업무» 한 칸(120)으로 줄인다 — 240 이면 타임라인이 100px 남짓만 남는다(#2687).
  const isMobile = useIsMobile();
  const leftWidth = isMobile ? LEFT_WIDTH_MOBILE : LEFT_WIDTH;
  const leftPad = isMobile ? '0 8px' : '0 12px';

  // ── 보이는 범위만 묻는다(서버는 coalesce 로 '기간 겹침' 을 본다). 배율은 범위를 바꾸지 않는다.
  useEffect(() => {
    if (!projectId) return;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const params = { sort: 'number', limit: GANTT_LIMIT, date_from: scale.from, date_to: scale.to };
    if (quick) params.quick = quick;
    if (q && q.trim()) params.q = q.trim();
    // 🔴 보낼 때 오류를 비운다 — 한 번 실패한 문구가 다음 성공 뒤에 남지 않게.
    setError('');
    taskPmAPI.listTasks(projectId, params)
      .then((res) => {
        if (seqRef.current !== seq) return;          // 지나간 요청의 응답은 버린다
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
        setTotal(Number(res?.data?.total || 0));
      })
      .catch((e) => {
        if (seqRef.current !== seq) return;
        // 🔴 실패하면 막대·안내를 비운다 — 옛 total 로 «처음 N개만» 이 같이 뜨지 않게.
        setItems([]);
        setTotal(0);
        setError(errorText(e, '간트 업무를 불러오지 못했습니다.'));
      });
  }, [projectId, quick, q, scale.from, scale.to, refreshKey, reloadKey]);

  // 잔디 간트의 왼쪽 열은 «상태 / 업무» 다 — 상태별로 묶고 빈 상태는 아예 그리지 않는다.
  const groups = useMemo(() => {
    const buckets = new Map();
    (statuses || []).forEach((s) => buckets.set(String(s.id), []));
    const orphans = [];
    items.forEach((t) => {
      const key = t.status_id == null ? null : String(t.status_id);
      if (key !== null && buckets.has(key)) buckets.get(key).push(t);
      else orphans.push(t);
    });
    // 묶음 안 순서 = 시작일 오름 → 같으면 번호 오름.
    const byStart = (a, b) => {
      const sa = barDates(a)?.start || '';
      const sb = barDates(b)?.start || '';
      if (sa !== sb) return sa < sb ? -1 : 1;
      return (a.number || 0) - (b.number || 0);
    };
    const out = [];
    (statuses || []).forEach((s) => {
      const rows = buckets.get(String(s.id)) || [];
      if (rows.length > 0) out.push({ key: `status-${s.id}`, label: s.label, tasks: rows.slice().sort(byStart) });
    });
    if (orphans.length > 0) out.push({ key: 'status-none', label: '상태 없음', tasks: orphans.slice().sort(byStart) });
    return out;
  }, [items, statuses]);

  // 🔴 effect(처음 그릴 때·단위·배율 변경)와 «오늘» 버튼이 같은 함수를 부른다 —
  //    상태를 바꾸지 않아도 버튼이 동작해야 한다(옆으로 스크롤한 뒤 되돌아오기).
  const scrollToToday = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (todayKey < scale.from || todayKey > scale.to) { el.scrollLeft = 0; return; }
    const left = leftWidth + diffDays(scale.from, todayKey) * scale.pxPerDay;
    const view = (el.clientWidth || 0);
    el.scrollLeft = Math.max(0, isMobile
      ? left - leftWidth - Math.max(0, view - leftWidth) * 0.3   // 모바일: 오늘선 = 고정 열 오른쪽 + 보이는 타임라인의 30%
      : left - view * 0.3);                                        // 데스크톱: 지금 그대로
  }, [scale, todayKey, leftWidth, isMobile]);

  useEffect(() => { scrollToToday(); }, [scrollToToday]);

  // ───────────────────────────────────────────── 막대 끌기(기간 변경)

  /**
   * 끌어 놓은 결과를 서버에 보낸다. 보내는 키는 '원래 있던 필드' 뿐이다
   * (마감일만 있던 업무를 옮겨도 시작일이 새로 생기지 않는다).
   */
  const saveDates = async (info, delta) => {
    const next = previewDates({ ...info, deltaDays: delta });
    const body = {};
    if (info.mode === 'start') {
      if (next.start !== info.origStart) body.start_date = next.start;
    } else if (info.mode === 'end') {
      if (next.end !== info.origEnd) body.due_date = next.end;
    } else {
      if (info.hadStart) body.start_date = next.start;
      if (info.hadDue) body.due_date = next.end;
    }
    // 반대편에 막혀 하루도 안 움직였으면 보내지 않는다.
    if (Object.keys(body).length === 0) return;

    savingRef.current.add(info.id);
    // 🔴 낙관 반영 '직전' 에 번호를 넘긴다 — 이미 나가 있는 목록 응답이 바뀐 날짜를 덮지 않게.
    seqRef.current += 1;
    setItems((prev) => prev.map((t) => (t.id === info.id ? { ...t, ...body } : t)));
    try {
      await taskPmAPI.updateTask(info.id, body);
      if (onChanged) onChanged();
    } catch (e) {
      // 실패하면 원래 날짜로 되돌리고, 서버 값으로 한 번 더 읽는다(위에서 번호를 넘겼다).
      setItems((prev) => prev.map((t) => (t.id === info.id
        ? { ...t, start_date: info.hadStart ? info.origStart : null, due_date: info.hadDue ? info.origEnd : null }
        : t)));
      setSaveError(errorText(e, '기간을 바꾸지 못했습니다.'));
      setReloadKey((k) => k + 1);
    } finally {
      savingRef.current.delete(info.id);
    }
  };

  // 몸통(move)·왼쪽 손잡이(start)·오른쪽 손잡이(end) 공용 누르기.
  const startDrag = (task, mode) => (e) => {
    // 🔴 아래 조건으로 빠져나가기 '전' 에 지운다 — 직전 끌기가 막대 밖에서 끝나 click 이 오지
    //    않았으면 삼킴 표시가 남아 다음 정상 클릭 한 번을 먹는다.
    suppressClickRef.current = false;
    if (mode !== 'move') e.stopPropagation();          // 손잡이는 몸통 끌기를 깨우지 않는다
    if (!canEdit || savingRef.current.has(task.id) || e.button !== 0) return;
    const dates = barDates(task);
    if (!dates) return;
    e.preventDefault();                                 // 끌 때 글자가 선택되지 않게
    setSaveError('');
    // 웹 배율 = 화면 px ÷ CSS px. 넓은 스크롤 상자로 재야 offsetWidth 정수 반올림 오차가 작다.
    // 🔴 이름이 rootScale 인 것은 `scale` 이 이미 간트 눈금 객체라서다.
    const el = scrollRef.current;
    const r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    const rootScale = r ? scaleOf(r.width, el.offsetWidth) : 1;
    dragRef.current = {
      id: task.id,
      mode,
      startX: e.clientX,
      rootScale,
      origStart: dates.start,
      origEnd: dates.end,
      hadStart: Boolean(task.start_date),
      hadDue: Boolean(task.due_date),
      deltaDays: 0,
    };
    setDrag({ ...dragRef.current });
  };

  // 끄는 동안의 mousemove·mouseup 은 document 에서 받는다(막대 밖으로 벗어나도 따라온다).
  const dragging = Boolean(drag);
  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const cur = dragRef.current;
      if (!cur) return;
      const delta = Math.round((e.clientX - cur.startX) / cur.rootScale / scale.pxPerDay);
      if (delta === cur.deltaDays) return;
      cur.deltaDays = delta;
      setDrag({ ...cur });
    };
    const onUp = (e) => {
      const cur = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!cur) return;
      // 🔴 이동량은 state 클로저가 아니라 이 이벤트의 clientX 로 다시 계산한다.
      const delta = Math.round((e.clientX - cur.startX) / cur.rootScale / scale.pxPerDay);
      if (delta === 0) return;            // 끌지 않았다 → 이어 오는 click 이 상세를 연다
      suppressClickRef.current = true;
      saveDates(cur, delta);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging, scale.pxPerDay, canEdit, onChanged]);

  const zoomIndex = Math.max(0, ZOOMS.indexOf(zoom));
  const canZoomOut = zoomIndex > 0;
  const canZoomIn = zoomIndex < ZOOMS.length - 1;

  // 색 = 유형 색(없으면 정보색). 캘린더와 같은 규칙이고, 간트만 같은 색 테두리를 더한다.
  const barColors = (task) => {
    const option = optionMap && optionMap.get ? optionMap.get(task.type_id) : null;
    if (option && option.color) return { background: `${option.color}33`, color: option.color };
    return { background: COLORS.infoSoft, color: COLORS.info };
  };

  // 불러오기 오류가 먼저다(막대가 아예 없는 상태라 저장 실패 문구보다 급하다).
  const alertText = error || saveError;

  const todayInRange = todayKey >= scale.from && todayKey <= scale.to;
  const todayLeft = todayInRange
    ? leftWidth + diffDays(scale.from, todayKey) * scale.pxPerDay + scale.pxPerDay / 2 - 1
    : null;

  const unitBtn = (on) => ({
    height: '23px', boxSizing: 'border-box', padding: '0 10px', border: 'none', borderRadius: '5px',
    background: on ? COLORS.surface : 'transparent', color: on ? COLORS.text : COLORS.textSub,
    fontSize: '12px', fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit',
  });
  const zoomBtn = (enabled) => ({
    ...btn('secondary', 'sm'), width: '32px', height: '32px', boxSizing: 'border-box', padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    opacity: enabled ? 1 : 0.5, cursor: enabled ? 'pointer' : 'default',
  });
  const leftCell = {
    width: `${leftWidth}px`, flexShrink: 0, boxSizing: 'border-box',
    position: 'sticky', left: 0, borderRight: `1px solid ${COLORS.border}`,
  };
  const laneCell = { width: `${scale.width}px`, flexShrink: 0, position: 'relative' };

  return (
    <section
      aria-label="업무 간트차트"
      style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, overflow: 'hidden' }}
    >
      {/* ── 도구줄(잔디 48) — 단위 묶음 · «오늘» · 축소·배율·확대 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '48px', boxSizing: 'border-box', padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div role="group" aria-label="단위" style={{ display: 'flex', gap: '2px', background: COLORS.bg, borderRadius: '6px', padding: '2px' }}>
          {UNITS.map((u) => (
            <button key={u.value} type="button" aria-pressed={unit === u.value}
              onClick={() => setUnit(u.value)} style={unitBtn(unit === u.value)}>{u.label}</button>
          ))}
        </div>
        <button type="button" onClick={scrollToToday}
          style={{ ...btn('secondary', 'sm'), height: '30px', boxSizing: 'border-box' }}>오늘</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
          <button type="button" aria-label="축소" disabled={!canZoomOut}
            onClick={() => setZoom(ZOOMS[zoomIndex - 1])} style={zoomBtn(canZoomOut)}>−</button>
          <span style={{ width: '40px', textAlign: 'center', fontSize: '12px', color: COLORS.textSub }}>{zoom}%</span>
          <button type="button" aria-label="확대" disabled={!canZoomIn}
            onClick={() => setZoom(ZOOMS[zoomIndex + 1])} style={zoomBtn(canZoomIn)}>+</button>
        </div>
      </div>

      {/* 🔴 스크롤 컨테이너는 이 하나다 — 머리는 sticky top, 왼쪽 열은 sticky left 로 제자리에 남는다. */}
      <div ref={scrollRef} data-gantt-scroll="true"
        style={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)', minHeight: '240px', position: 'relative' }}>
        <div style={{ width: `${leftWidth + scale.width}px`, position: 'relative' }}>

          {/* ── 날짜 머리(61) */}
          <div style={{ display: 'flex', height: `${HEAD_HEIGHT}px`, position: 'sticky', top: 0, zIndex: 2, background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
            <div data-gantt-leftcol="true" style={{ ...leftCell, zIndex: 3, background: COLORS.surface, display: 'flex', alignItems: 'center', padding: leftPad, fontSize: '12px', color: COLORS.textSub }}>
              {isMobile ? '업무' : '상태 / 업무'}
            </div>
            <div style={{ width: `${scale.width}px`, flexShrink: 0, position: 'relative' }}>
              {scale.top.map((c) => (
                <div key={`top-${c.key}`} style={{
                  position: 'absolute', top: 0, left: `${c.left}px`, width: `${c.width}px`, height: `${HEAD_TOP_HEIGHT}px`,
                  boxSizing: 'border-box', borderLeft: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center',
                  padding: '0 6px', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', overflow: 'hidden',
                }}>{c.label}</div>
              ))}
              {scale.bottom.map((c) => (
                <div key={`bottom-${c.key}`} style={{
                  position: 'absolute', top: `${HEAD_TOP_HEIGHT}px`, left: `${c.left}px`, width: `${c.width}px`, height: `${HEAD_BOTTOM_HEIGHT}px`,
                  boxSizing: 'border-box', borderLeft: `1px solid ${COLORS.borderSoft}`, borderTop: `1px solid ${COLORS.borderSoft}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden',
                  color: c.weekend ? (c.sub === '일' ? COLORS.danger : COLORS.info) : COLORS.textSub,
                }}>{c.sub ? `${c.label} ${c.sub}` : c.label}</div>
              ))}
            </div>
          </div>

          {/* ── 본문(묶음 머리 + 업무 행). 세로 눈금선·오늘선은 뒤에 깔린다. */}
          <div style={{ position: 'relative' }}>
            <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: `${leftWidth}px`, width: `${scale.width}px`, height: '100%', zIndex: 0, pointerEvents: 'none' }}>
              {scale.bottom.map((c) => (
                <div key={`grid-${c.key}`} style={{
                  position: 'absolute', top: 0, left: `${c.left}px`, width: `${c.width}px`, height: '100%',
                  boxSizing: 'border-box', borderLeft: `1px solid ${COLORS.borderSoft}`,
                  background: c.weekend ? COLORS.bg : 'transparent',
                }} />
              ))}
            </div>
            {todayLeft !== null && (
              <div aria-hidden="true" data-gantt-today="true" style={{
                position: 'absolute', top: 0, left: `${todayLeft}px`, width: '2px', height: '100%',
                background: COLORS.danger, zIndex: 0, pointerEvents: 'none',
              }} />
            )}

            {groups.map((g) => (
              <div key={g.key}>
                <div data-group-head="true" aria-label={`${g.label} ${g.tasks.length}건`}
                  style={{ display: 'flex', height: `${GROUP_HEIGHT}px`, background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}` }}>
                  <div data-gantt-leftcol="true" style={{ ...leftCell, zIndex: 1, background: COLORS.bg, display: 'flex', alignItems: 'center', gap: '6px', padding: leftPad }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</span>
                    <span style={{ fontSize: '12px', color: COLORS.textSub, flexShrink: 0 }}>{g.tasks.length}</span>
                  </div>
                  <div style={{ width: `${scale.width}px`, flexShrink: 0 }} />
                </div>

                {g.tasks.map((t) => {
                  const onBar = Boolean(drag && drag.id === t.id);
                  // 끄는 동안에는 '놓으면 될 자리' 를 그린다(손잡이는 반대편에서 멈춘다).
                  const dates = onBar ? previewDates(drag) : barDates(t);
                  const geo = geometryOf(dates, scale);
                  const colors = barColors(t);
                  const barLabel = dates ? `${t.key} ${t.title} ${dates.start}~${dates.end}` : `${t.key} ${t.title}`;
                  const open = () => { if (onOpenTask) onOpenTask(t.id); };
                  // 손잡이는 쓰기 권한이 있고 시작·마감이 둘 다 있을 때만 — 하루 막대는 이동만 한다
                  // (한쪽만 있는 업무에 손잡이를 주면 보이는 모양과 저장 결과가 어긋난다).
                  const withHandles = Boolean(canEdit && t.start_date && t.due_date);
                  return (
                    <div key={t.id} style={{ display: 'flex', height: `${ROW_HEIGHT}px`, borderBottom: `1px solid ${COLORS.borderSoft}` }}>
                      <div data-gantt-leftcol="true" style={{ ...leftCell, zIndex: 1, background: COLORS.surface }}>
                        <button type="button" aria-label={`${t.key} ${t.title}`} title={`${t.key} ${t.title}`} onClick={open}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', height: '100%', minWidth: 0, boxSizing: 'border-box', padding: leftPad, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                          {/* 모바일은 키를 칸에서 뺀다(제목만 · 키는 aria-label·title 에 남는다). */}
                          {!isMobile && <span style={{ fontSize: '11px', color: COLORS.textMuted, flexShrink: 0 }}>{t.key}</span>}
                          <span style={{ fontSize: '13px', color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                        </button>
                      </div>
                      <div style={laneCell}>
                        {geo && (
                          <>
                            {/* 🔴 여는 길은 이 onClick 하나다(canEdit 과 무관 — viewer 도 상세를 연다).
                                끌기는 onMouseDown 이 시작하고, 끈 직후의 click 한 번은 여기서 삼킨다. */}
                            <div role="button" tabIndex={0} aria-label={barLabel} title={barLabel}
                              onClick={() => {
                                if (suppressClickRef.current) { suppressClickRef.current = false; return; }
                                open();
                              }}
                              onMouseDown={startDrag(t, 'move')}
                              onKeyDown={(e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                if (e.key === ' ') e.preventDefault();      // 스페이스로 화면이 내려가지 않게
                                open();
                              }}
                              style={{
                                position: 'absolute', top: '4px', left: `${geo.left}px`, width: `${geo.width}px`,
                                height: `${BAR_HEIGHT}px`, boxSizing: 'border-box', borderRadius: `${BAR_RADIUS}px`,
                                border: `1px solid ${colors.color}`, background: colors.background, color: colors.color,
                                opacity: t.is_done ? 0.55 : 1,
                                cursor: canEdit ? (onBar ? 'grabbing' : 'grab') : 'pointer',
                              }}>
                              {withHandles && (
                                <>
                                  <div data-handle="start" aria-hidden="true" onMouseDown={startDrag(t, 'start')}
                                    style={{ position: 'absolute', top: 0, left: 0, width: `${HANDLE_WIDTH}px`, height: '100%', cursor: 'ew-resize' }} />
                                  <div data-handle="end" aria-hidden="true" onMouseDown={startDrag(t, 'end')}
                                    style={{ position: 'absolute', top: 0, right: 0, width: `${HANDLE_WIDTH}px`, height: '100%', cursor: 'ew-resize' }} />
                                </>
                              )}
                            </div>
                            {/* 이름은 막대 '밖' 오른쪽에 둔다(잔디와 같다) — 읽기 전용이라 포커스를 받지 않는다.
                                끄는 동안에는 그 자리에 바뀔 기간을 보여 준다. */}
                            <div aria-hidden="true" style={{
                              position: 'absolute', top: '4px', left: `${geo.left + geo.width + 6}px`,
                              height: `${BAR_HEIGHT}px`, lineHeight: `${BAR_HEIGHT}px`, fontSize: '12px',
                              color: COLORS.textSub, whiteSpace: 'nowrap', pointerEvents: 'none',
                              opacity: t.is_done ? 0.55 : 1,
                            }}>{onBar && dates ? `${dates.start}~${dates.end}` : t.title}</div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 불러오기 오류와 저장 실패를 같은 자리에 그린다(저장 실패는 다시 읽어도 남는다). */}
      {alertText && (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, margin: '12px 16px' }}>{alertText}</div>
      )}
      {!alertText && items.length === 0 && (
        <p style={{ margin: '12px 16px 0', fontSize: '13px', color: COLORS.textSub, textAlign: 'center' }}>
          이 범위에 시작일·마감일이 있는 업무가 없습니다.
        </p>
      )}
      {total > items.length && (
        <p style={{ margin: '8px 16px 12px', fontSize: '12px', color: COLORS.textSub }}>
          이 간트 범위에서 처음 {items.length}개만 표시합니다(전체 {total}개).
        </p>
      )}
    </section>
  );
}
